(ns storrito.ui.button
  #?(:cljs
     (:require [storrito.app.message :as message]
               [storrito.ui.tooltip :as tooltip]))
  (:require [clojure.string :as str]))

(def css-default ["inline-flex"
                  "items-center"
                  "border"
                  "rounded"
                  "shadow-sm"
                  "focus:outline-none"
                  "focus:ring-2"
                  "focus:ring-offset-2"
                  "font-medium"
                  "rounded-md"
                  "justify-center"
                  "align-center"
                  "cursor-pointer"
                  "disabled:cursor-not-allowed"
                  "disabled:bg-gray-200"
                  "disabled:text-gray-500"])

;; SIZES
(def css-xs
  ["px-2.5" "py-1.5" "text-xs"])

(def css-sm
  ["px-3" "py-2" "text-sm" "leading-4"])

(def css-md
  ["px-4" "py-2" "text-md"])

(def css-lg
  ["px-6" "py-3" "text-base"])


;; BUTTON TYPES
(def css-primary-disabled
  ["!cursor-not-allowed" "!bg-secondary-100" "!text-gray-200"])

(def css-secondary-disabled
  ["!cursor-not-allowed" "!bg-gray-200" "!text-gray-500"])

(def css-primary
  ["text-white" "bg-secondary-500" "hover:bg-secondary-300"
   "focus:ring-secondary-500"
   "disabled:cursor-not-allowed" "disabled:!bg-secondary-100" "disabled:!text-gray-200"])

(def css-secondary
  ["border-gray-300" "hover:border-gray-400" "font-medium" "rounded"
   "text-gray-800" "bg-white" "hover:bg-gray-50" "focus:ring-1"
   "focus:ring-offset-gray-800" "focus:ring-secondary-800" "focus:outline-none"])

(def css-cancel
  ["border-gray-300" "hover:border-gray-400" "font-medium" "rounded"
   "text-gray-800" "bg-white" "hover:bg-gray-50" "focus:ring-1"
   "focus:ring-offset-gray-800" "focus:ring-secondary-800" "focus:outline-none"])

(def css-primary-full-screen
  ["text-white" "bg-secondary-400" "hover:bg-secondary-300" "focus:ring-secondary-500"
   "w-full" "justify-center" "px-6" "py-3" "text-xl"
   "disabled:cursor-not-allowed" "disabled:!bg-secondary-100" "disabled:!text-gray-200"])

(def css-success
  ["bg-success-400" "border-transparent" "text-white"])

(def css-danger
  ["text-white" "bg-red-500" "hover:bg-red-400" "focus:ring-red-600"
   "disabled:bg-red-200"
   "disabled:cursor-not-allowed" "disabled:!bg-red-200" "disabled:!text-red-400"])

(def css-danger-outline
  ["inline-flex" "items-center" "text-red-600" "rounded" "shadow-sm"
   "bg-red-100" "hover:bg-red-200" "focus:ring-red-400"
   "border" "border-red-600" "hover:text-red-600"
   "disabled:cursor-not-allowed" "disabled:!bg-red-200" "disabled:!text-red-400" "disabled:border-red-400"])

(def css-danger-disabled
  ["!cursor-not-allowed" "!bg-red-200"])

(def css-warning
  ["text-white" "bg-yellow-500" "hover:bg-yellow-400" "focus:ring-yellow-600"
   "disabled:cursor-not-allowed" "disabled:bg-yellow-200" "disabled:text-yellow-500"])

(def css-warning-outline
  ["inline-flex" "items-center" "text-yellow-700" "rounded" "shadow-sm"
   "bg-yellow-100" "hover:bg-yellow-200" "focus:ring-yellow-400"
   "border" "border-yellow-600"
   "disabled:cursor-not-allowed" "disabled:bg-yellow-200" "disabled:text-yellow-500"])

(def css-warning-disabled
  ["!cursor-not-allowed" "!bg-yellow-200" "!text-yellow-500"])

(def css-info
  ["text-black" "bg-white" "hover:bg-white-300" "focus:ring-white-500"])

(def css-icon-active
  ["bg-secondary-400" "hover:bg-secondary-300" "text-white"])

(def css-icon-inactive
  ["bg-gray-200" "hover:bg-gray-100" "text-gray-700"])

;; The ".rounded-md" presumably was a typo in the original string,
;; so we separate it into two classes: "font-medium" and "rounded-md".
(def css-icon
  ["justify-center" "align-center" "inline-flex" "items-center" "shadow-sm"
   "leading-4" "font-medium" "rounded-md" "focus:outline-none"
   "disabled:cursor-not-allowed" "disabled:bg-gray-100" "disabled:text-gray-500"])

(def css-icon-md
  ["px-3" "py-2" "h-10" "w-10"])

(def css-icon-xs
  ["h-7" "w-7" "px-2.5" "py-1.5"])

(def css-transparent
  ["text-white" "p-2" "bg-white" "bg-opacity-20"
   "hover:bg-opacity-30" "rounded" "text-sm" "md:text-base"])

(def css-sidebar
  ["bg-yellow-100" "hover:bg-yellow-200" "border-yellow-800" "p-3" "w-full"])

(def button-primary-css
  (concat css-default
          css-primary
          css-md))

(def button-secondary-css
  (concat css-default
       css-secondary
       css-md))

(def button-success-css
  (concat
    css-default
    css-success
    css-md))

(def button-cancel-css
  (concat css-default
          css-cancel
          css-md))

(defn vectorize
  "Helper function to avoid migration of every single button that
   utilizes the `:class` attribute with a string to add a css class to a button."
  [css-class]
  (if-not (vector? css-class)
    (str/split css-class
               #" ")
    css-class))

(defn button-primary
  [params & content]
  (let [css (concat (vectorize (:class params))
                    button-primary-css)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-primary-xs
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-primary
                    css-xs)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-primary-sm
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-primary
                    css-sm)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-primary-lg
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-primary
                    css-lg)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-primary-full
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-primary-full-screen)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-secondary
  [params & content]
  (let [css (concat (vectorize (:class params))
                    button-secondary-css)
        updated-params (assoc params :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-secondary-xs
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-secondary
                    css-xs)
        updated-params (assoc params :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-secondary-sm
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-secondary
                    css-sm)
        updated-params (assoc params :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-secondary-lg
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-secondary
                    css-lg)
        updated-params (assoc params :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-cancel
  ([params] (button-cancel params "Cancel"))
  ([params & content]
   (let [disabled? (:disabled params)
         css (concat (vectorize (:class params))
                     css-default
                     css-cancel
                     css-md
                     (when disabled?
                       css-secondary-disabled))
         updated-params (assoc params :class css)]
     (apply conj
            [:div
             updated-params]
            content))))

(defn button-cancel-sm
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-cancel
                    css-sm)
        updated-params (assoc params :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-icon-square
  [params & content]
  (let [active? (:active params)
        css (concat (vectorize (:class params))
                    (if active?
                      css-icon-active
                      css-icon-inactive)
                    css-icon
                    css-icon-md)
        updated-params (assoc params :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(def icon-square-xs
  (concat css-icon-inactive
          css-icon
          css-icon-xs))

(defn button-icon-square-xs
  [params & content]
  (let [active? (:active params)
        css (concat (vectorize (:class params))
                    (if active?
                      css-icon-active
                      css-icon-inactive)
                    css-icon
                    css-icon-xs)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-success [params & content]
  (let [css (concat (vectorize (:class params))
                    button-success-css)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-danger [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-danger
                    css-md)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-danger-outline [params & content]
  (let [css (concat (vectorize (:class params))
                    css-danger-outline
                    css-md)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(def button-danger-outline-sm-class
  (concat css-danger-outline
          css-sm))

(defn button-danger-outline-sm
  [params & content]
  (let [css (concat (vectorize (:class params))
                    button-danger-outline-sm-class)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-warning [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-warning
                    css-md)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-warning-outline-sm
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-warning-outline
                    css-sm)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-info
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-default
                    css-info
                    css-md)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-transparent
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-transparent)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn button-sidebar
  [params & content]
  (let [css (concat (vectorize (:class params))
                    css-sidebar)
        updated-params (assoc params
                              :class css)]
    (apply conj
           [:button
            updated-params]
           content)))

(defn a-button
  [params & content]
  (let [disabled? (:disabled params)
        css (concat (vectorize (:class params))
                    css-default
                    css-secondary
                    css-md
                    (when disabled?
                      css-secondary-disabled))
        updated-params (assoc params :class css)]
    (apply conj
           [:a
            updated-params]
           content)))

(defn a-button-primary
  [params & content]
  (let [disabled? (:disabled params)
        css (concat (vectorize (:class params))
                    css-default
                    css-primary
                    css-md
                    (when disabled?
                      css-primary-disabled))
        updated-params (assoc params :class css)]
    (apply conj
           [:a
            updated-params]
           content)))

(defn icon-cancel
  ([params-btn] (icon-cancel params-btn {}))
  ([params-btn params-icon]
   [:button params-btn
    [:i.fas.fa-times-circle.text-black.cursor-pointer params-icon]]))

#?(:cljs
   (defn information-button
     [message]
     [:span {:onClick (fn [e]
                        (.preventDefault e)
                        (message/show-message!
                          {:type :info
                           :duration 30000
                           :content
                           [:div message]}))}
      [:i.fa.fa-question-circle.ml-2.cursor-pointer]]))

#?(:cljs
   (defn move-button
     "Moves the selected gallery items into the current folder."
     [on-click]
     [:<>
      [button-primary
       {:class "mr-2"
        :data-for "move-button"
        :data-tip true
        :onClick on-click}
       "Move here"]
      [tooltip/tooltip {:id "move-button"
                        :message "Moves the selected items into the current folder."}]]))
